<script lang="ts" setup>
const { modelValue, message = undefined } = defineProps<{
  modelValue: boolean;
  message?: Message;
}>();

const emit = defineEmits(["update:modelValue"]);

const { reportForm, resetReportForm, reportSend } = useReport();
const validationErrors = reactive<Record<string, string[]>>({});

const errorMessage = ref("");

const pending = ref(false);
const sent = ref(false);

const subjects = reactive([
  { value: "", label: "- Kies een onderwerp" },
  { value: "Offensive content", label: "Aanstootgevende inhoud" },
  { value: "Incorrect information", label: "Onjuiste informatie" },
  { value: "Other", label: "Anders" },
]);

const updateValue = (value: boolean) => {
  emit("update:modelValue", value);
};

const closeModal = () => {
  updateValue(false);
};

const onSubmit = async () => {
  // Set pending state
  pending.value = true;
  // Send form
  try {
    if (message?.id) {
      await reportSend({
        ...reportForm,
        chat_message_id: message?.id,
      });
      // Set sent state
      sent.value = true;
    } else {
      closeModal();
    }
  } catch (e: unknown) {
    const error = useApiError(e);
    // Show errors to user
    if (error.isValidationError) {
      Object.assign(validationErrors, error.bag);
      // reportForm.value?.setErrors(err.bag);
    } else {
      errorMessage.value =
        "Er is een fout opgetreden bij het verzenden van de melding. Probeer het later opnieuw.";
    }
  }
  // Reset pending state
  pending.value = false;
};

watch(
  () => modelValue,
  (value) => {
    if (value) {
      // Clear form when modal reopens
      sent.value = false;
      // Reset form
      resetReportForm();
      // Clear validation errors
      Object.keys(validationErrors).forEach(
        (key) => delete validationErrors[key],
      );
    }
  },
);
</script>

<template>
  <ModalWindow :model-value="modelValue" @update:model-value="updateValue">
    <FormElement @submit="onSubmit">
      <template v-if="sent">
        <div class="header">
          <BaseHeading class="title"> Feedback verzonden </BaseHeading>
          <p class="intro">
            We hebben je feedback ontvangen en gaan er zo snel mogelijk mee aan
            de slag.
          </p>
        </div>
        <BaseButtonGroup>
          <BaseButton @click="closeModal">Sluiten</BaseButton>
        </BaseButtonGroup>
      </template>

      <template v-else>
        <ChatMessage
          v-if="message"
          :id="message.id"
          class="message"
          :content="message.content"
          :time="message.time"
          :role="message.role"
          :line-clamp="3"
        ></ChatMessage>

        <div class="header">
          <BaseHeading class="title">Feedback </BaseHeading>
          <p class="intro">
            We horen graag jouw feedback zodat we de assistent kunnen blijven
            verbeteren.
          </p>
        </div>

        <FormFieldset>
          <FormField>
            <FormLabel required :invalid="!!validationErrors.subject">
              Onderwerp
            </FormLabel>
            <FormSelect
              v-model="reportForm.subject"
              theme="overlay"
              :options="subjects"
              :invalid="!!validationErrors.subject"
              :disabled="pending"
              required
              @input="delete validationErrors.subject"
            />
          </FormField>
          <FormField>
            <FormLabel required :invalid="!!validationErrors.description">
              Meer informatie
            </FormLabel>
            <FormTextarea
              v-model="reportForm.description"
              type="text"
              theme="overlay"
              :invalid="!!validationErrors.description"
              :disabled="pending"
              required
              class="description"
              @input="delete validationErrors.description"
            />
          </FormField>

          <FormErrorMessage :error="errorMessage" />

          <FormValidationErrors
            :errors="validationErrors"
            title="Controleer het formulier"
          />
        </FormFieldset>

        <BaseButtonGroup>
          <BaseButton type="submit" theme="overlay" :disabled="pending">{{
            pending ? "Laden..." : "Versturen"
          }}</BaseButton>
          <BaseButton
            variant="ghost"
            theme="overlay"
            :disabled="pending"
            @click="closeModal"
            >Annuleren</BaseButton
          >
        </BaseButtonGroup>
      </template>
    </FormElement>
  </ModalWindow>
</template>

<style lang="scss" scoped>
.header {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.description {
  height: 10rem;
}
</style>
